export const VARIABLE_KINDS = { BODY: 'body', BUTTON: 'button' };

export const MEDIA_TYPES = {
  IMAGE: 'image',
  VIDEO: 'video',
  DOCUMENT: 'document',
};

const MEDIA_ACCEPT = {
  [MEDIA_TYPES.IMAGE]: 'image/jpeg,image/png,image/webp',
  [MEDIA_TYPES.VIDEO]: 'video/mp4,video/3gpp',
  [MEDIA_TYPES.DOCUMENT]:
    'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain',
};

export const MEDIA_MAX_SIZE_MB = {
  [MEDIA_TYPES.IMAGE]: 5,
  [MEDIA_TYPES.VIDEO]: 15,
  [MEDIA_TYPES.DOCUMENT]: 15,
};

export const templateBody = template =>
  (template && (template.text || template.body || template.label)) || '';

export const templateName = template => (template && template.name) || '';

export const templateLanguage = template =>
  (template && (template.language || template.lang)) || '';

const languageKey = template =>
  templateLanguage(template).toLowerCase().replace(/-/g, '_');

export const templateMatches = (candidate, template) =>
  templateName(candidate) === templateName(template) &&
  languageKey(candidate) === languageKey(template);

export const templatePrimaryLanguage = template =>
  `${templateLanguage(template)}`.split(/[_-]/)[0].toUpperCase();

export const templateKey = template => {
  if (!template) return '';
  if (template.id !== undefined && template.id !== null) {
    return `${template.id}`;
  }
  return `${templateName(template)}::${templateLanguage(template)}`;
};

export const templateCategoryKey = template => {
  if (!template) return '';
  const raw = template.category;
  if (raw === undefined || raw === null || raw === '') return '';
  if (typeof raw === 'number' || /^\d+$/.test(`${raw}`)) {
    const num = Number(raw);
    if (num === 1) return 'service';
    if (num === 2) return 'marketing';
    if (num === 3) return 'authentication';
    return `${raw}`;
  }
  const key = `${raw}`.trim().toLowerCase();
  if (key === 'utility') return 'service';
  return key;
};

export const CATEGORY_ORDER = ['marketing', 'service', 'authentication'];
export const LANGUAGE_ORDER = ['en', 'he', 'ua', 'uk', 'it', 'fr', 'de'];

export const sortByPriority = (values, priority) => {
  const rank = value => {
    const index = priority.indexOf(`${value}`.toLowerCase().split(/[_-]/)[0]);
    return index < 0 ? priority.length : index;
  };
  return [...values].sort(
    (a, b) => rank(a) - rank(b) || `${a}`.localeCompare(`${b}`)
  );
};

export const isTemplateApproved = template => {
  if (!template) return false;
  if (template.approved === true) return true;
  if (template.approved === false) return false;
  const status = template.status;
  if (status === undefined || status === null || status === '') return true;
  return `${status}`.trim().toLowerCase() === 'approved';
};

export const templatesHaveStatus = (templates = []) =>
  templates.some(
    template =>
      template &&
      ((template.status !== undefined && template.status !== null) ||
        template.approved !== undefined)
  );

export const templateHeaderMediaType = template => {
  if (!template) return null;
  const headerType = Number(template.headerType);
  if (headerType === 3) return MEDIA_TYPES.IMAGE;
  if (headerType === 4) return MEDIA_TYPES.VIDEO;
  if (headerType === 5) return MEDIA_TYPES.DOCUMENT;

  const headerFormat = `${
    template.headerFormat || template.header_format || ''
  }`
    .trim()
    .toLowerCase();
  if (headerFormat === 'image') return MEDIA_TYPES.IMAGE;
  if (headerFormat === 'video') return MEDIA_TYPES.VIDEO;
  if (headerFormat === 'document') return MEDIA_TYPES.DOCUMENT;

  const allowed = `${template.allowedAttachment || ''}`.toLowerCase();
  if (!allowed) return null;
  if (allowed.includes('image/')) return MEDIA_TYPES.IMAGE;
  if (allowed.includes('video/')) return MEDIA_TYPES.VIDEO;
  return MEDIA_TYPES.DOCUMENT;
};

export const templateRequiresMedia = template =>
  !!templateHeaderMediaType(template);

const EXTENSION_TYPES = new Map([
  ['jpg', 'image/jpeg'],
  ['jpeg', 'image/jpeg'],
  ['png', 'image/png'],
  ['webp', 'image/webp'],
  ['mp4', 'video/mp4'],
  ['3gp', 'video/3gpp'],
  ['3gpp', 'video/3gpp'],
  ['pdf', 'application/pdf'],
  ['doc', 'application/msword'],
  [
    'docx',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ],
  ['xls', 'application/vnd.ms-excel'],
  ['xlsx', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
  ['ppt', 'application/vnd.ms-powerpoint'],
  [
    'pptx',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  ],
  ['txt', 'text/plain'],
]);

const typeFromName = name =>
  (name.includes('.') && EXTENSION_TYPES.get(name.split('.').pop())) || '';

export const typedFile = file => {
  const type = !file.type && typeFromName(`${file.name || ''}`.toLowerCase());
  return type
    ? new File([file], file.name, { type, lastModified: file.lastModified })
    : file;
};

const matchesAcceptToken = (token, type, name) => {
  if (token.startsWith('.')) return name.endsWith(token);
  if (token.endsWith('/*')) return type.startsWith(token.slice(0, -1));
  return type === token;
};

export const fileMatchesAccept = (accept, file) => {
  const tokens = `${accept || ''}`
    .split(',')
    .map(token => token.trim().toLowerCase())
    .filter(Boolean);
  if (!tokens.length) return true;
  const name = `${file.name || ''}`.toLowerCase();
  const type = `${file.type || ''}`.toLowerCase() || typeFromName(name);
  return tokens.some(token => matchesAcceptToken(token, type, name));
};

export const templateMediaAccept = template => {
  const type = templateHeaderMediaType(template);
  if (!type) return '';
  const types = MEDIA_ACCEPT[type].split(',');
  const allowed = template.allowedAttachment;
  const narrowed = allowed
    ? types.filter(mime => fileMatchesAccept(allowed, { type: mime, name: '' }))
    : [];
  return (narrowed.length ? narrowed : types).join(',');
};

export const templateHasWebsiteVariable = template =>
  !!template && Number(template.callToActionWebsiteType) === 2;

export const templateHasWebsiteVariable2 = template =>
  !!template && Number(template.callToActionWebsiteType2) === 2;

export const templateBodyVariables = template => {
  const body = templateBody(template);
  const found = new Set();
  const regex = /\{\{\s*(\d+)\s*\}\}/g;
  let match = regex.exec(body);
  while (match) {
    found.add(match[1]);
    match = regex.exec(body);
  }
  return Array.from(found).sort((a, b) => Number(a) - Number(b));
};

export const templateVariables = template => {
  const variables = templateBodyVariables(template).map(variable => ({
    variable,
    kind: VARIABLE_KINDS.BODY,
  }));
  if (templateHasWebsiteVariable(template)) {
    variables.push({ variable: '1', kind: VARIABLE_KINDS.BUTTON });
  }
  if (templateHasWebsiteVariable2(template)) {
    variables.push({ variable: '2', kind: VARIABLE_KINDS.BUTTON });
  }
  return variables;
};

export const templateHasVariables = template =>
  templateVariables(template).length > 0;

export const MAX_QUICK_REPLIES = 10;

const QUICK_REPLY_FIELD = /^quickReply\d+$/;

const buttonListQuickReplies = buttons =>
  (Array.isArray(buttons) ? buttons : [])
    .filter(
      button =>
        button &&
        `${button.type || ''}`.toUpperCase() === 'QUICK_REPLY' &&
        `${button.text || ''}`.trim()
    )
    .slice(0, MAX_QUICK_REPLIES)
    .map((button, position) => ({
      index: position + 1,
      text: `${button.text}`.trim(),
    }));

export const templateQuickReplies = template => {
  if (!template) return [];
  const replies = [];
  for (let index = 1; index <= MAX_QUICK_REPLIES; index += 1) {
    const raw = template[`quickReply${index}`];
    const text = raw === undefined || raw === null ? '' : `${raw}`.trim();
    if (text) replies.push({ index, text });
  }
  return replies.length ? replies : buttonListQuickReplies(template.buttons);
};

export const PREVIEW_ACTION_FIELDS = [
  'callToActionWebsiteText',
  'callToActionWebsiteText2',
  'callToActionCallText',
  'couponTitle',
];

export const templateButtonFields = template =>
  Object.fromEntries(
    Object.entries(template || {}).filter(
      ([key]) =>
        key === 'buttons' ||
        QUICK_REPLY_FIELD.test(key) ||
        PREVIEW_ACTION_FIELDS.includes(key)
    )
  );

export const templateHasButtonInfo = template =>
  Object.keys(templateButtonFields(template)).length > 0;

const buttonTexts = (buttons, type) =>
  (Array.isArray(buttons) ? buttons : [])
    .filter(
      button =>
        button &&
        `${button.type || ''}`.toUpperCase() === type &&
        `${button.text || ''}`.trim()
    )
    .map(button => `${button.text}`.trim());

const buttonListActionTexts = buttons => {
  const [website, website2] = buttonTexts(buttons, 'URL');
  return Object.fromEntries(
    Object.entries({
      callToActionWebsiteText: website,
      callToActionWebsiteText2: website2,
      callToActionCallText: buttonTexts(buttons, 'PHONE_NUMBER')[0],
      couponTitle: buttonTexts(buttons, 'COPY_CODE')[0],
    }).filter(([, text]) => text)
  );
};

export const templateDetailFields = template => ({
  ...buttonListActionTexts(template?.buttons),
  ...templateButtonFields(template),
  footer: typeof template?.footer === 'string' ? template.footer : '',
});

export const templateMissesActionText = template =>
  !!template &&
  ((Number(template.callToActionWebsiteType) > 0 &&
    !template.callToActionWebsiteText) ||
    (Number(template.callToActionWebsiteType2) > 0 &&
      !template.callToActionWebsiteText2));

export const templateHasDetailInfo = template =>
  templateHasButtonInfo(template) &&
  typeof template.footer === 'string' &&
  !templateMissesActionText(template);

export const templateHasQuickReplies = template =>
  templateQuickReplies(template).length > 0;

const PREVIEW_HEADERS = {
  [MEDIA_TYPES.IMAGE]: 2,
  [MEDIA_TYPES.VIDEO]: 3,
  [MEDIA_TYPES.DOCUMENT]: 4,
};

export const templatePreviewHeader = template =>
  PREVIEW_HEADERS[templateHeaderMediaType(template)] || 1;

const escapeHtml = text =>
  `${text}`
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

export const templateBodyAsHtml = template =>
  escapeHtml(templateBody(template))
    .replace(/```([^`]+)```/g, '<code>$1</code>')
    .replace(
      /(^|[\s(])\*([^*\n]+)\*(?=$|[\s.,!?;:)])/g,
      '$1<strong>$2</strong>'
    )
    .replace(/(^|[\s(])_([^_\n]+)_(?=$|[\s.,!?;:)])/g, '$1<em>$2</em>')
    .replace(/(^|[\s(])~([^~\n]+)~(?=$|[\s.,!?;:)])/g, '$1<s>$2</s>')
    .replace(/\r?\n/g, '<br>');
